/**
 * Firebase Auth errors, translated for the people who actually read them.
 *
 * Firebase's `error.message` is written for whoever is building the app —
 * "Firebase: Error (auth/invalid-credential)." — and showing it to a customer
 * who simply mistyped their password is both confusing and a small information
 * leak. Every auth surface routes its errors through `authErrorMessage`.
 *
 * The customer site carries a near-identical copy at
 * socialsnap.pics/src/utils/authErrors.js. The only difference is who a stuck
 * person is told to ask for help: an administrator here, support there.
 * Change one, change the other.
 *
 * Two rules this file exists to enforce:
 *
 *  1. A failed sign-in never says WHICH half was wrong. Firebase stopped
 *     distinguishing user-not-found from wrong-password when email enumeration
 *     protection became the default (it returns auth/invalid-credential for
 *     both), and we shouldn't reintroduce the distinction.
 *  2. Nothing that looks like a Firebase string reaches a screen — including
 *     codes added after this file was written. Unmapped codes fall back to the
 *     caller's generic message rather than leaking the raw text.
 */

import * as Sentry from '@sentry/react';

const SIGN_IN_FAILED = 'Incorrect email or password. Please try again.';

const MESSAGES = {
  // Sign-in. auth/invalid-credential is what a modern project returns for a
  // wrong password AND for an email that was never registered; the two legacy
  // codes still appear on older projects. All three say the same thing.
  'auth/invalid-credential': SIGN_IN_FAILED,
  'auth/invalid-login-credentials': SIGN_IN_FAILED,
  'auth/wrong-password': SIGN_IN_FAILED,
  'auth/user-not-found': SIGN_IN_FAILED,

  // Input shape
  'auth/invalid-email': 'That doesn’t look like a valid email address.',
  'auth/missing-email': 'Enter your email address.',
  'auth/missing-password': 'Enter your password.',
  'auth/weak-password': 'Choose a password at least 6 characters long.',
  'auth/email-already-in-use': 'An account with this email already exists. Try signing in instead.',

  // Account state
  'auth/user-disabled': 'This account has been disabled. Ask an administrator to restore your access.',
  'auth/requires-recent-login': 'For your security, please sign in again before making this change.',

  // Rate limiting and connectivity
  'auth/too-many-requests':
    'Too many failed attempts. Wait a few minutes before trying again, or reset your password.',
  'auth/network-request-failed': 'We couldn’t reach the server. Check your connection and try again.',
  'auth/timeout': 'That took too long. Please try again.',

  // Email links (password reset, email verification)
  'auth/expired-action-code': 'This link has expired. Request a new one and try again.',
  'auth/invalid-action-code':
    'This link is no longer valid — it may already have been used. Request a new one.',

  // Social / popup sign-in
  'auth/popup-blocked': 'Your browser blocked the sign-in window. Allow popups for this site and try again.',
  'auth/popup-closed-by-user': 'Sign-in was cancelled.',
  'auth/cancelled-popup-request': 'Sign-in was cancelled.',
  'auth/account-exists-with-different-credential':
    'An account already exists with this email using a different sign-in method.',

  // Callable-function failures. Account setup (invites, redeeming a link) runs
  // through Cloud Functions, so those surfaces raise `functions/` codes rather
  // than `auth/` ones. A deliberate HttpsError message written by our own
  // backend still wins — it survives the passthrough check below — but the
  // bare gRPC statuses do not.
  'functions/internal': 'Something went wrong on our end. Please try again.',
  'functions/unavailable': 'The service is briefly unavailable. Please try again in a moment.',
  'functions/deadline-exceeded': 'That took too long. Please try again.',
  'functions/unauthenticated': 'Please sign in and try again.',

  // Misconfiguration — a person can't fix these, so say so plainly rather than
  // implying they did something wrong. See AUTH_CONFIG_ERROR_CODES below.
  'auth/operation-not-allowed': 'That sign-in method isn’t available right now. Please try another way.',
  'auth/unauthorized-domain': 'Sign-in isn’t available from this address. Please contact an administrator.',
  'auth/internal-error': 'Something went wrong on our end. Please try again.'
};

/**
 * Codes that mean the app is misconfigured rather than the person being
 * wrong — worth a Sentry report, unlike a mistyped password. `auth/` prefixed,
 * so callers that skip reporting on all auth errors need this exception.
 */
export const AUTH_CONFIG_ERROR_CODES = new Set([
  'auth/operation-not-allowed',
  'auth/unauthorized-domain',
  'auth/internal-error',
  'auth/invalid-api-key',
  'auth/app-deleted',
  'auth/configuration-not-found'
]);

/**
 * Report an auth failure to Sentry — but not the ones people cause themselves.
 *
 * Expected, and therefore silent:
 *  - any `auth/` code that isn't in AUTH_CONFIG_ERROR_CODES. A mistyped
 *    password, an expired link, a closed popup; reporting those would bury the
 *    ones that matter under noise from every fat-fingered login.
 *  - `app/unauthorized`, the dashboard's bounce for a signed-in person without
 *    a role. Working as designed.
 *
 * Everything else reports: the `auth/` codes that mean the PROJECT is
 * misconfigured (sign-in method switched off, domain not authorized — nobody
 * signing in can fix those), and any non-auth fault that surfaced here, such
 * as the Firestore write or callable behind account setup failing.
 *
 * Call it next to authErrorMessage wherever auth can fail. Returns whether it
 * reported, which is mostly useful to tests.
 *
 * @param {unknown} error   the caught error
 * @param {object} context  extra Sentry tags, e.g. { page: 'login' }
 */
export const reportAuthError = (error, context = {}) => {
  if (!error) return false;

  const code = error.code;
  const expected =
    (typeof code === 'string' && code.startsWith('auth/') && !AUTH_CONFIG_ERROR_CODES.has(code)) ||
    code === 'app/unauthorized';
  if (expected) return false;

  Sentry.captureException(error, {
    tags: { area: 'auth', auth_code: code || 'none', ...context }
  });
  return true;
};

/**
 * True for text Firebase generated. Its messages are either prefixed
 * ("Firebase: ...") or carry the code in parentheses — "Password should be at
 * least 6 characters (auth/weak-password)." A callable that fails without a
 * message of its own surfaces the bare gRPC status instead, e.g. "INTERNAL".
 * Every one of those shapes was written for a developer.
 */
const isFirebaseMessage = (message) =>
  message.startsWith('Firebase:') ||
  /\((auth|app|functions)\/[a-z-]+\)/.test(message) ||
  /^[A-Z][A-Z_ ]*$/.test(message);

/**
 * A message worth showing someone.
 *
 * Order matters:
 *
 *  1. `error.userFacing` — an error we built with a message MORE specific than
 *     anything the code alone could say, e.g. "An account already exists with
 *     this email. Please sign in with Google." Generalising that back down to
 *     the table's wording would lose the useful half.
 *  2. The mapped code.
 *  3. An error we threw without that flag but with a human message anyway —
 *     the dashboard's unauthorized-role bounce, or an HttpsError our backend
 *     wrote.
 *  4. The caller's fallback, which is also where every unmapped `auth/` code
 *     and every Firebase-shaped string lands.
 *
 * @param {unknown} error    the caught error
 * @param {string} fallback  what to say when nothing better is known
 */
export const authErrorMessage = (error, fallback = 'Something went wrong. Please try again.') => {
  const message = typeof error?.message === 'string' ? error.message.trim() : '';
  const usable = message && !isFirebaseMessage(message);

  if (error?.userFacing && usable) return message;

  const mapped = MESSAGES[error?.code];
  if (mapped) return mapped;

  return usable ? message : fallback;
};

/**
 * Did the person close the sign-in popup themselves? Not an error worth
 * showing — the UI should just go quiet.
 */
export const isCancelledPopup = (error) =>
  error?.code === 'auth/popup-closed-by-user' || error?.code === 'auth/cancelled-popup-request';

export default authErrorMessage;
